import { useState, type FormEvent, type ReactNode } from "react";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { ApiError } from "@njbsoft/api-client";
import { TurnstileWidget } from "@/components/turnstile-widget";
import { Button, buttonClasses, FormField, inputClass } from "@/components/ui";
import {
  acceptInviteErrorKey,
  useAcceptAccountInvite,
  useAccountInvitePreview,
  useResendAccountInviteByToken,
} from "@/hooks/use-account-invite";
import { useMyCustomerContext } from "@/hooks/use-customer-context";
import { usePortalBranding } from "@/hooks/use-portal-branding";
import { useTurnstileGate } from "@/hooks/use-turnstile-gate";
import { ACCEPT_INVITE_TOKEN_RE, acceptInviteCtaTargets } from "@/lib/account-invite-links";
import { signOut, signOutExpiredSession } from "@/lib/auth";
import { formatLongInstantDay } from "@/lib/dates";
import { resolveInviteView, type InviteView } from "@/lib/invite-view";
import { completeSignIn } from "@/lib/sign-in";
import { useCustomerStore } from "@/store";

const MIN_PASSWORD_LENGTH = 8;

interface AcceptInviteSearch {
  token?: string;
}

export const Route = createFileRoute("/portal/$slug/accept-invite")({
  // A value that cannot be a token never reaches the wire: posting it would
  // only spend a rate-limit slot to be told so.
  validateSearch: (search: Record<string, unknown>): AcceptInviteSearch => ({
    token:
      typeof search.token === "string" && ACCEPT_INVITE_TOKEN_RE.test(search.token)
        ? search.token
        : undefined,
  }),
  component: AcceptInvitePage,
});

/**
 * The resolver's views plus the two only a SUBMIT can reach: an address the
 * server cannot provision, and an account it created without a session.
 */
type PageView = InviteView | { kind: "addressUnavailable" } | { kind: "provisioned" };

const TITLE = "text-xl font-semibold tracking-[-0.01em] text-ink";
const BODY = "text-sm text-body";
const FAILURE = "text-sm text-tone-red";
const CTA_LINK = `${buttonClasses("primary")} w-full`;

/**
 * The day an instant fell on where the reader is, long-month in the page's
 * language. The preview carries no utility zone, so the reader's own stands in.
 */
function readerDay(iso: string, language: string): string {
  return formatLongInstantDay(iso, Intl.DateTimeFormat().resolvedOptions().timeZone, language);
}

/**
 * The email-link landing for a customer-portal invite, under the inviting
 * utility's slug. The route is PUBLIC and the token is the whole payload:
 * possession of it is the authorization, and the server binds the membership
 * to the INVITED address no matter who is looking at the page.
 *
 * Every sentence naming the invitee interpolates the PREVIEW's email. The
 * session is not a source for it — a customer login returns no user object and
 * GET /me does not resolve for a non-member, so reading the identity from the
 * session renders an empty name to the one person who needs it.
 */
function AcceptInvitePage() {
  const { t, i18n } = useTranslation("customer");
  const { slug } = Route.useParams();
  const { token } = Route.useSearch();
  const navigate = useNavigate();
  const gate = useTurnstileGate();
  const signedIn = useCustomerStore((s) => s.isAuthenticated);
  const preview = useAccountInvitePreview(token, gate.token);
  // Already in the cache: the layout renders this page only once it answered.
  const branding = usePortalBranding(slug).data;
  // A signed-out read here answers 401, which the shared client turns into a
  // session teardown and a hard navigation off the invite link.
  const { data: ctx } = useMyCustomerContext({ enabled: signedIn });
  const acceptMutation = useAcceptAccountInvite();
  const resendMutation = useResendAccountInviteByToken();
  // Terminal by design: nothing ever resets this back to null. Every exit
  // from the addressUnavailable, wrongIdentity and signIn views leaves the
  // page (a CTA, a navigation), so there is no return trip to clear it.
  const [override, setOverride] = useState<PageView | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  // Set when the server refuses a resend as closed: the preview said the
  // window was open and it shut between the read and the click.
  const [resendClosed, setResendClosed] = useState(false);
  // Covers the window a mutation's isPending does NOT: gate.freshToken()
  // resets the widget and waits on the next solve for up to
  // FRESH_TOKEN_TIMEOUT_MS, and for that whole span no mutation has started,
  // so a second click would burn a second challenge and issue a second
  // submit. Every submit path sets this before awaiting anything.
  const [submitting, setSubmitting] = useState(false);

  const invite = preview.data;
  // The invite names its utility; before the preview answers, or when it
  // cannot, the page's own branding does.
  const orgName = invite?.org_name || branding?.org_name || "";
  const busy = submitting || acceptMutation.isPending || resendMutation.isPending;

  // Five failures change the VIEW or end the session rather than print a
  // sentence under the button — a view carries its own body, so adding the
  // mapper's near-duplicate sentence under it says the same thing twice.
  // Everything else renders the mapper's copy.
  const applyFailure = (error: unknown) => {
    if (error instanceof ApiError) {
      if (error.status === 401) {
        // The bearer did not validate, or its profile is no longer live.
        // The same exit the shared client takes on its own 401s: back to
        // this link, signed out, with sign-in on offer and the token intact.
        signOutExpiredSession();
        return;
      }
      if (error.code === "invite_address_unavailable") {
        setOverride({ kind: "addressUnavailable" });
        return;
      }
      if (error.code === "invite_resend_closed") {
        setResendClosed(true);
        return;
      }
      if (error.code === "invite_wrong_identity") {
        setOverride({ kind: "wrongIdentity" });
        return;
      }
      if (error.code === "invite_requires_sign_in") {
        setOverride({ kind: "signIn" });
        return;
      }
    }
    setFormError(t(acceptInviteErrorKey(error), { email: invite?.email ?? "", orgName }));
  };

  const runAccept = async (extra: { password?: string; full_name?: string }) => {
    if (token === undefined || submitting) return;
    setSubmitting(true);
    setFormError(null);
    try {
      const turnstile_token = await gate.freshToken();
      const result = await acceptMutation.mutateAsync({
        token,
        turnstile_token,
        ...extra,
      });
      if (!result.tokens && !signedIn) {
        // Provisioned, but the session could not be issued after the
        // commit: the account exists and the password just chosen works at
        // this utility's sign-in. The link is spent, so nothing here retries
        // it.
        setOverride({ kind: "provisioned" });
        return;
      }
      // Finishing the sign-in for the account the invite added makes it the
      // active and the remembered one, even beside another account at this
      // utility, whether the accept issued a session or the person was
      // already signed in.
      await completeSignIn(result.tokens ?? null, slug, result.account_id);
      void navigate({ to: "/home" });
    } catch (error) {
      applyFailure(error);
    } finally {
      setSubmitting(false);
    }
  };

  const runResend = async () => {
    if (token === undefined || submitting) return;
    setSubmitting(true);
    setFormError(null);
    try {
      const turnstile_token = await gate.freshToken();
      await resendMutation.mutateAsync({ token, turnstile_token });
    } catch (error) {
      applyFailure(error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleCreateSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const data = new FormData(event.currentTarget);
    const fullName = String(data.get("full_name") ?? "").trim();
    const password = String(data.get("password") ?? "");
    const confirm = String(data.get("confirm_password") ?? "");

    const errors: Record<string, string> = {};
    if (fullName === "") errors.full_name = t("acceptInvite.validation.nameRequired");
    if (password.length < MIN_PASSWORD_LENGTH) {
      errors.password = t("acceptInvite.validation.passwordTooShort", {
        min: MIN_PASSWORD_LENGTH,
      });
    }
    if (password !== confirm) {
      errors.confirm_password = t("acceptInvite.validation.passwordMismatch");
    }
    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) return;

    void runAccept({ full_name: fullName, password });
  };

  const renderContent = (): ReactNode => {
    // Only a wire 404 — or no token at all — means the link is dead. Every
    // other preview failure is the visitor's moment, not the link's, and
    // gets the sentence for it plus a way back.
    if (token === undefined || preview.error?.status === 404) {
      return (
        <>
          <h2 className={TITLE}>{t("acceptInvite.invalidTitle")}</h2>
          <p className={FAILURE}>{t("acceptInvite.invalidBody", { orgName })}</p>
        </>
      );
    }

    if (preview.isError) {
      return (
        <>
          <h2 className={TITLE}>{t("acceptInvite.errorTitle")}</h2>
          <p className={FAILURE}>{t(acceptInviteErrorKey(preview.error), { orgName })}</p>
          {/* A reload, not a refetch: every Turnstile response is
              single-use, and the mount solve is the one path that mints a
              new one before the read goes out. */}
          <Button variant="secondary" className="w-full" onClick={() => window.location.reload()}>
            {t("acceptInvite.retryCta")}
          </Button>
        </>
      );
    }

    if (invite === undefined) {
      return (
        <>
          <h2 className={TITLE}>{t("acceptInvite.loadingTitle")}</h2>
          <p className="text-sm text-muted">{t("acceptInvite.loading")}</p>
        </>
      );
    }

    const view =
      override ??
      resolveInviteView({
        status: invite.status,
        profileExists: invite.profile_exists,
        signedIn,
        sessionEmail: ctx?.profile?.email ?? null,
        invitedEmail: invite.email,
      });
    const { landingHref, signIn } = acceptInviteCtaTargets(slug, token);
    const failure = formError === null ? null : <p className={FAILURE}>{formError}</p>;

    switch (view.kind) {
      case "create":
        return (
          <>
            <h2 className={TITLE}>{t("acceptInvite.createTitle")}</h2>
            <p className={BODY}>
              {t("acceptInvite.createBody", {
                orgName,
                email: invite.email,
                accountName: invite.account_name,
              })}
            </p>
            <form className="space-y-4" onSubmit={handleCreateSubmit} noValidate>
              {/* Each input carries an explicit aria-label: a required
                  FormField renders the label text plus a "*" inside its
                  <label>, so that text is not the field's usable accessible
                  name and no label query resolves without this. */}
              <FormField
                label={t("acceptInvite.fullNameLabel")}
                required
                error={fieldErrors.full_name}
              >
                {/* maxLength mirrors the wire schema (full_name 200,
                    password 128). Without these an over-long value reaches
                    the validator, which answers a 400 with NO code — and the
                    wire's error-body contract reads every uncoded 400 on
                    these ops as "malformed link", which is the wrong sentence
                    for a too-long name. Capping entry is what keeps the token
                    the only reachable validator rejection. */}
                <input
                  name="full_name"
                  type="text"
                  aria-label={t("acceptInvite.fullNameLabel")}
                  autoComplete="name"
                  maxLength={200}
                  defaultValue={invite.full_name ?? ""}
                  className={inputClass}
                />
              </FormField>
              <FormField
                label={t("acceptInvite.passwordLabel")}
                required
                error={fieldErrors.password}
              >
                <input
                  name="password"
                  type="password"
                  aria-label={t("acceptInvite.passwordLabel")}
                  autoComplete="new-password"
                  maxLength={128}
                  className={inputClass}
                />
              </FormField>
              <FormField
                label={t("acceptInvite.confirmLabel")}
                required
                error={fieldErrors.confirm_password}
              >
                <input
                  name="confirm_password"
                  type="password"
                  aria-label={t("acceptInvite.confirmLabel")}
                  autoComplete="new-password"
                  maxLength={128}
                  className={inputClass}
                />
              </FormField>
              {failure}
              <Button type="submit" className="w-full" disabled={busy}>
                {busy ? t("acceptInvite.creating") : t("acceptInvite.createCta")}
              </Button>
            </form>
          </>
        );

      case "signIn":
        return (
          <>
            <h2 className={TITLE}>{t("acceptInvite.signInTitle")}</h2>
            <p className={BODY}>
              {t("acceptInvite.signInBody", {
                email: invite.email,
                accountName: invite.account_name,
              })}
            </p>
            {failure}
            <Link {...signIn} className={CTA_LINK}>
              {t("acceptInvite.signInCta")}
            </Link>
          </>
        );

      case "accept":
        return (
          <>
            <h2 className={TITLE}>{t("acceptInvite.acceptTitle")}</h2>
            <p className={BODY}>
              {t("acceptInvite.acceptBody", {
                orgName,
                accountName: invite.account_name,
              })}
            </p>
            {failure}
            <Button className="w-full" onClick={() => void runAccept({})} disabled={busy}>
              {busy ? t("acceptInvite.accepting") : t("acceptInvite.acceptCta")}
            </Button>
          </>
        );

      case "wrongIdentity":
        return (
          <>
            <h2 className={TITLE}>{t("acceptInvite.wrongIdentityTitle")}</h2>
            <p className={BODY}>{t("acceptInvite.wrongIdentityBody", { email: invite.email })}</p>
            {failure}
            <Button
              className="w-full"
              onClick={() => signOut({ revoke: true, redirectTo: landingHref })}
            >
              {t("acceptInvite.signOutCta")}
            </Button>
          </>
        );

      case "expired": {
        if (resendMutation.isSuccess) {
          return (
            <>
              <h2 className={TITLE}>{t("acceptInvite.resentTitle")}</h2>
              <p className={BODY}>{t("acceptInvite.resentBody", { email: invite.email })}</p>
            </>
          );
        }
        // The server's own resend gate, read off the preview or answered on
        // the click: the page never offers an action the server will refuse.
        const canResend = invite.resend_available && !resendClosed;
        return (
          <>
            <h2 className={TITLE}>{t("acceptInvite.expiredTitle")}</h2>
            {canResend ? (
              <>
                <p className={BODY}>{t("acceptInvite.expiredBody", { email: invite.email })}</p>
                {failure}
                <Button className="w-full" onClick={() => void runResend()} disabled={busy}>
                  {busy ? t("acceptInvite.resending") : t("acceptInvite.resendCta")}
                </Button>
              </>
            ) : (
              <p className={BODY}>{t("acceptInvite.expiredClosedBody", { orgName })}</p>
            )}
          </>
        );
      }

      case "accepted":
        return (
          <>
            <h2 className={TITLE}>{t("acceptInvite.acceptedTitle")}</h2>
            <p className={BODY}>{t("acceptInvite.acceptedBody")}</p>
            {invite.accepted_at && (
              <p className="text-sm text-muted">
                {t("acceptInvite.acceptedOn", {
                  date: readerDay(invite.accepted_at, i18n.language),
                })}
              </p>
            )}
            <Link to="/portal/$slug" params={{ slug }} className={CTA_LINK}>
              {t("acceptInvite.signInCta")}
            </Link>
          </>
        );

      case "revoked":
        return (
          <>
            <h2 className={TITLE}>{t("acceptInvite.revokedTitle")}</h2>
            <p className={BODY}>{t("acceptInvite.revokedBody", { orgName })}</p>
          </>
        );

      case "addressUnavailable":
        return (
          <>
            <h2 className={TITLE}>{t("acceptInvite.addressUnavailableTitle")}</h2>
            {/* No sign-in exit: the address holds an auth user with no live
                profile, so that login cannot succeed. */}
            <p className={BODY}>{t("acceptInvite.addressUnavailableBody", { orgName })}</p>
          </>
        );

      case "provisioned":
        return (
          <>
            <h2 className={TITLE}>{t("acceptInvite.provisionedTitle")}</h2>
            <p className={BODY}>{t("acceptInvite.provisionedBody", { email: invite.email })}</p>
            <Link to="/portal/$slug" params={{ slug }} className={CTA_LINK}>
              {t("acceptInvite.signInCta")}
            </Link>
          </>
        );
    }
  };

  return (
    <div className="space-y-4">
      {renderContent()}
      {/* Mounted once, outside the switch: remounting the widget would spend a
          fresh challenge on every state change and drop the handle the submit
          paths reset through. */}
      {gate.enabled && (
        <TurnstileWidget siteKey={gate.siteKey} onToken={gate.onToken} ref={gate.widgetRef} />
      )}
    </div>
  );
}
